import React from 'react';

function AlertTemplate({ title, message, styling, content }) {
  // ✅ STAGE 1 FIX: Use VITE_API_URL from env (works in dev + production)
  const API_URL = import.meta.env.VITE_API_URL || window.location.origin;
  const fontFamily = styling?.fontFamily || 'Inter';
  const bgColor = styling?.backgroundColor || '#ef4444';
  const accentColor = styling?.accentColor || '#fca5a5';
  const textColor = styling?.textColor || '#FFFFFF';
  const mainImage = styling?.mainImage || styling?.announcementImage;
  
  // Editable content with defaults
  const alertBadge = content?.alertBadge || 'Important Notice';
  
  // ✅ STAGE 4: QR Code settings
  const showQR = content?.ctaEnabled === true;
  const whatsappNumber = '2349055444495';
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=https://wa.me/${whatsappNumber}`;

  // ✅ Check if media is video
  const isVideo = mainImage && (
    mainImage.endsWith('.mp4') ||
    mainImage.endsWith('.webm') ||
    mainImage.endsWith('.ogg')
  );

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}dd 100%)`,
        fontFamily: `${fontFamily}, sans-serif`,
        padding: '2.5rem',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Alert Border */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          border: '15px solid rgba(255, 255, 255, 0.3)',
          pointerEvents: 'none',
          zIndex: 10,
          animation: 'pulse 2s ease-in-out infinite',
        }}
      />

      {/* Warning Stripes Background */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 100px,
            rgba(255, 255, 255, 0.03) 100px,
            rgba(255, 255, 255, 0.03) 200px
          )`,
          zIndex: 0,
        }}
      />

      {/* Top Corner Decorations */}
      <div
        style={{
          position: 'absolute',
          top: '3rem',
          left: '3rem',
          display: 'flex',
          gap: '12px',
        }}
      >
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            style={{
              width: '15px',
              height: '60px',
              background: `rgba(255, 255, 255, ${0.3 - i * 0.08})`,
              transform: `skewX(-15deg)`,
            }}
          />
        ))}
      </div>

      {/* Alert Icon */}
      <div
        style={{
          width: '110px',
          height: '110px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '2rem',
          border: '4px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 15px 50px rgba(0, 0, 0, 0.3)',
          animation: 'pulse 2s ease-in-out infinite',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div
          style={{
            fontSize: '4.5rem',
          }}
        >
          ⚠️
        </div>
      </div>

      {/* Alert Badge */}
      <div
        style={{
          display: 'inline-block',
          padding: '0.7rem 2rem',
          background: 'rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(10px)',
          borderRadius: '50px',
          marginBottom: '1.5rem',
          border: '2px solid rgba(255, 255, 255, 0.4)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <span
          style={{
            fontSize: '1rem',
            fontWeight: '800',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: textColor,
          }}
        >
          Important Notice
        </span>
      </div>

      {/* Main Title */}
      <h1
        style={{
          fontSize: styling?.fontSize === 'small' ? '2.8rem' : styling?.fontSize === 'large' ? '4.2rem' : '3.5rem',
          fontWeight: '900',
          textAlign: 'center',
          color: textColor,
          marginBottom: '2.5rem',
          maxWidth: '1000px',
          lineHeight: '1.2',
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {title || 'IMPORTANT NOTICE'}
      </h1>

      {/* Image/Video Section */}
      {mainImage && (
        <div
          style={{
            position: 'relative',
            marginBottom: '3rem',
            maxWidth: '700px',
            width: '100%',
            zIndex: 1,
          }}
        >
          {/* Warning Frame */}
          <div
            style={{
              position: 'absolute',
              top: '-12px',
              left: '-12px',
              right: '12px',
              bottom: '12px',
              background: `repeating-linear-gradient(
                45deg,
                ${accentColor},
                ${accentColor} 10px,
                transparent 10px,
                transparent 20px
              )`,
              borderRadius: '20px',
              opacity: 0.4,
            }}
          />

          <div
            style={{
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
              position: 'relative',
              border: '4px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            {isVideo ? (
              <video
                src={`${API_URL}${mainImage}`}
                autoPlay
                loop
                muted
                playsInline
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
            ) : (
              <img
                src={`${API_URL}${mainImage}`}
                alt="Alert"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* Message Box */}
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(15px)',
          borderRadius: '25px',
          padding: '2.5rem 3.5rem',
          maxWidth: '950px',
          width: '100%',
          boxShadow: '0 15px 50px rgba(0, 0, 0, 0.3)',
          border: '3px solid rgba(255, 255, 255, 0.3)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Corner Accent Stripes */}
        <div
          style={{
            position: 'absolute',
            top: '0',
            left: '0',
            width: '50px',
            height: '50px',
            background: `repeating-linear-gradient(
              -45deg,
              ${accentColor},
              ${accentColor} 5px,
              transparent 5px,
              transparent 10px
            )`,
            borderTopLeftRadius: '25px',
            opacity: 0.5,
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '0',
            right: '0',
            width: '50px',
            height: '50px',
            background: `repeating-linear-gradient(
              -45deg,
              ${accentColor},
              ${accentColor} 5px,
              transparent 5px,
              transparent 10px
            )`,
            borderBottomRightRadius: '25px',
            opacity: 0.5,
          }}
        />

        <p
          style={{
            fontSize: '1.8rem',
            lineHeight: '1.6',
            fontWeight: '600',
            color: textColor,
            textAlign: 'center',
            margin: 0,
          }}
        >
          {message || 'Please pay attention to this important announcement'}
        </p>
      </div>

      {/* ✅ STAGE 4: QR Code - Conditional */}
      {showQR && (
        <div
          style={{
            marginTop: '2.5rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.8rem',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <div
            style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: textColor,
              opacity: 0.9,
            }}
          >
            Scan to Chat
          </div>
          <img
            src={qrCodeUrl}
            alt="WhatsApp QR Code"
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              padding: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            }}
          />
        </div>
      )}

      {/* Bottom Warning Icons */}
      <div
        style={{
          position: 'absolute',
          bottom: '4rem',
          display: 'flex',
          gap: '3rem',
          opacity: 0.3,
          zIndex: 1,
        }}
      >
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            style={{
              fontSize: '2rem',
              animation: `pulse 2s ease-in-out infinite ${i * 0.2}s`,
            }}
          >
            ⚠️
          </div>
        ))}
      </div>

      {/* Corner Decorations - Bottom Right */}
      <div
        style={{
          position: 'absolute',
          bottom: '3rem',
          right: '3rem',
          display: 'flex',
          gap: '12px',
        }}
      >
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            style={{
              width: '15px',
              height: '60px',
              background: `rgba(255, 255, 255, ${0.3 - i * 0.08})`,
              transform: `skewX(15deg)`,
            }}
          />
        ))}
      </div>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Roboto:wght@400;500;700;900&display=swap');
          
          @keyframes pulse {
            0%, 100% { 
              transform: scale(1);
              opacity: 1;
            }
            50% { 
              transform: scale(1.05);
              opacity: 0.85;
            }
          }
        `}
      </style>
    </div>
  );
}

export default AlertTemplate;